import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewEncapsulation,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  PaginationBarComponent,
  PaginationData,
} from '../../../../shared/components/pagination-bar/pagination-bar.component';
import {
  MembershipRewashEligibilityState,
  OrderRequestHistoryActionKind,
  OrderRequestHistoryRow,
  OrderRequestRewashTarget,
  OrderRequestTodayBannerState,
  PrepaidRewashEligibilityState,
  RewashEligibilityState,
} from '../../models/notification-order-request.models';
import { ORDER_REQUEST_HISTORY_PAGE_SIZE_OPTIONS } from '../../services/notification-order-request-history.service';
import {
  classifyHistoryRowAction,
  formatRewashAvailableUntilTime,
  resolveHeaderRewashTarget,
  resolveHistoryWashRewashTarget,
} from '../../utils/notification-order-request-history.util';
import { MembershipStatusStripComponent } from '../membership-status-strip/membership-status-strip.component';
import { MembershipStatusStripView } from '../../utils/membership-status-strip.util';

/**
 * Shared wash-history insight card + expandable table (sales panel + Active subscription).
 */
@Component({
  selector: 'app-wash-history-panel',
  standalone: true,
  imports: [CommonModule, PaginationBarComponent, MembershipStatusStripComponent],
  templateUrl: './wash-history-panel.component.html',
  styleUrls: ['./wash-history-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None,
})
export class WashHistoryPanelComponent implements OnChanges, OnDestroy {
  @Input() isLoading = false;
  @Input() banner: OrderRequestTodayBannerState | null = null;
  @Input() historyExpanded = false;
  @Input() rewashEligibility: RewashEligibilityState | null = null;
  @Input() membershipRewashEligibility: MembershipRewashEligibilityState | null = null;
  /** CWS-1882: rewash candidates for prepaid visits, which have no order of their own. */
  @Input() prepaidRewashEligibility: PrepaidRewashEligibilityState | null = null;
  /** CWS-1650: refused / wash-limit status shown at the top of the card. */
  @Input() statusStrip: MembershipStatusStripView | null = null;

  @Output() historyToggle = new EventEmitter<void>();
  @Output() pageChange = new EventEmitter<number>();
  @Output() pageSizeChange = new EventEmitter<number>();
  @Output() rewashClick = new EventEmitter<OrderRequestRewashTarget>();

  readonly pageSizeOptions = ORDER_REQUEST_HISTORY_PAGE_SIZE_OPTIONS;
  readonly starValues = [1, 2, 3, 4, 5];

  private countdownIntervalId: ReturnType<typeof setInterval> | null = null;
  private headerWindowExpired = false;

  get headerRewashTarget(): OrderRequestRewashTarget | null {
    return resolveHeaderRewashTarget(
      this.banner?.headerWash,
      this.rewashEligibility,
      this.membershipRewashEligibility,
      this.prepaidRewashEligibility
    );
  }

  get showRewashCta(): boolean {
    const target = this.headerRewashTarget;
    if (!target) {
      return false;
    }
    if (!target.expiresAt) {
      return true;
    }
    return !this.headerWindowExpired;
  }

  get showRewashCountdown(): boolean {
    return this.showRewashCta && !!this.headerRewashTarget?.expiresAt;
  }

  get formattedRewashAvailableUntil(): string {
    const time = formatRewashAvailableUntilTime(this.headerRewashTarget?.expiresAt);
    if (!time) {
      return '';
    }
    return `Rewash Available · Until ${time}`;
  }

  get visitsTotalLabel(): string {
    const total = this.banner?.totalVisits ?? 0;
    return `${total} visits total`;
  }

  isStarFilled(star: number): boolean {
    const rating = this.banner?.feedbackRating;
    if (rating == null) {
      return false;
    }
    return star <= rating;
  }

  get historyPaginationData(): PaginationData | null {
    const hp = this.banner?.historyPagination;
    if (!hp) {
      return null;
    }
    return {
      currentPage: hp.page,
      totalPages: hp.totalPages,
      totalItems: hp.total,
      itemsPerPage: hp.limit,
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['banner'] ||
      changes['rewashEligibility'] ||
      changes['membershipRewashEligibility'] ||
      changes['prepaidRewashEligibility']
    ) {
      this.syncHeaderCountdown();
    }
  }

  ngOnDestroy(): void {
    this.clearCountdown();
  }

  rowActionKind(row: OrderRequestHistoryRow): OrderRequestHistoryActionKind {
    return classifyHistoryRowAction(
      row,
      this.rewashEligibility,
      this.membershipRewashEligibility,
      this.prepaidRewashEligibility
    );
  }

  onHeaderRewashClick(): void {
    const target = this.headerRewashTarget;
    if (!target || !this.showRewashCta) {
      return;
    }
    this.rewashClick.emit({ ...target });
  }

  onRowRewashClick(row: OrderRequestHistoryRow): void {
    const target = resolveHistoryWashRewashTarget(
      row,
      this.rewashEligibility,
      this.membershipRewashEligibility,
      this.prepaidRewashEligibility
    );
    if (!target) {
      return;
    }
    this.rewashClick.emit(target);
  }

  private syncHeaderCountdown(): void {
    this.clearCountdown();
    this.headerWindowExpired = false;
    const expiresAt = this.headerRewashTarget?.expiresAt;
    if (!expiresAt) {
      return;
    }
    this.updateHeaderWindowExpired();
    this.countdownIntervalId = setInterval(() => {
      this.updateHeaderWindowExpired();
    }, 1000);
  }

  private updateHeaderWindowExpired(): void {
    const expiresAt = this.headerRewashTarget?.expiresAt;
    if (!expiresAt) {
      this.headerWindowExpired = false;
      return;
    }
    const expiresMs = new Date(expiresAt).getTime();
    if (Number.isNaN(expiresMs)) {
      this.headerWindowExpired = false;
      this.clearCountdown();
      return;
    }
    this.headerWindowExpired = Date.now() >= expiresMs;
    if (this.headerWindowExpired) {
      this.clearCountdown();
    }
  }

  private clearCountdown(): void {
    if (this.countdownIntervalId != null) {
      clearInterval(this.countdownIntervalId);
      this.countdownIntervalId = null;
    }
  }
}
